import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { getCoachesDualPanelSelector, getStudentsDualPanelSelector } from '../actions/usersActions'
import { getProgramsDualPanelSelectorThunk } from '../actions/programActions'
import { getAllSeries } from '../actions/seriesActions'
import { isEmpty } from 'lodash'

const studentsEntityAdapter = createEntityAdapter()
const unavailableStudentsEntityAdapter = createEntityAdapter()
const programsEntityAdapter = createEntityAdapter()
const seriesEntityAdapter = createEntityAdapter()
const coachesEntityAdapter = createEntityAdapter()


const initialState = {
  students: {
    isLoading: true,
    searchValue: '',
    items: studentsEntityAdapter.getInitialState(),
    unavailableItems: unavailableStudentsEntityAdapter.getInitialState(),
    pagination: { page: 0, isLastPage: false },
  },
  programs: {
    isLoading: true,
    searchValue: '',
    items: programsEntityAdapter.getInitialState(),
    pagination: { page: 0, isLastPage: false },
  },
  series: {
    isLoading: true,
    searchValue: '',
    items: seriesEntityAdapter.getInitialState(),
    pagination: { page: 0, isLastPage: false },
  },
  coaches: {
    isLoading: true,
    searchValue: '',
    items: coachesEntityAdapter.getInitialState(),
    pagination: { page: 0, isLastPage: false },
  },
}

const dualPanelSelectorReducer = createSlice({
  name: 'dualPanelSelector',
  initialState,
  reducers: {
    setUnavailableStudents: (state, action) => {
      unavailableStudentsEntityAdapter.setAll(state.students.unavailableItems, action.payload)
    },
    setSearchValue: (state, action) => {
      const key = action.payload.key
      if (state[key]) {
        state[key].searchValue = action.payload.searchValue
      }
    },
    reset: () => initialState,
  },
  extraReducers: builder => {
    builder
    // Get students
      .addCase(getStudentsDualPanelSelector.pending, state => {
        state.students.isLoading = true
      })
      .addCase(getStudentsDualPanelSelector.fulfilled, (state, action) => {
        const pagination = action.payload.meta.pagination
        const students = action.payload.students
        if (isEmpty(pagination) || pagination.page === 1) {
          studentsEntityAdapter.setAll(state.students.items, students)
        } else {
          studentsEntityAdapter.addMany(state.students.items, students)
        }
        state.students.isLoading = false
        state.students.pagination = pagination
      })

    // Get programs
      .addCase(getProgramsDualPanelSelectorThunk.pending, state => {
        state.programs.isLoading = true
      })
      .addCase(getProgramsDualPanelSelectorThunk.fulfilled, (state, action) => {
        const pagination = action.payload.meta.pagination
        const programs = action.payload.courses
        if (isEmpty(pagination) || pagination.page === 1) {
          programsEntityAdapter.setAll(state.programs.items, programs)
        } else {
          programsEntityAdapter.addMany(state.programs.items, programs)
        }
        state.programs.pagination = pagination
        state.programs.isLoading = false
      })

    // Get series
      .addCase(getAllSeries.pending, state => {
        state.series.isLoading = true
      })
      .addCase(getAllSeries.fulfilled, (state, action) => {
        const pagination = action.payload.meta.pagination
        const series = action.payload.series
        if (isEmpty(pagination) || pagination.page === 1) {
          seriesEntityAdapter.setAll(state.series.items, series)
        } else {
          seriesEntityAdapter.addMany(state.series.items, series)
        }
        state.series.pagination = pagination
        state.series.isLoading = false
      })

    // Get coaches
      .addCase(getCoachesDualPanelSelector.pending, state => {
        state.coaches.isLoading = true
      })
      .addCase(getCoachesDualPanelSelector.fulfilled, (state, action) => {
        const pagination = action.payload.meta.pagination
        const coaches = action.payload.coaches
        if (isEmpty(pagination) || pagination.page === 1) {
          coachesEntityAdapter.setAll(state.coaches.items, coaches)
        } else {
          coachesEntityAdapter.addMany(state.coaches.items, coaches)
        }
        state.coaches.pagination = pagination
        state.coaches.isLoading = false
      })
  },
})

const {
  selectAll: selectAllStudents,
} = studentsEntityAdapter.getSelectors(state => state.dualPanelSelector.students.items)

const {
  selectAll: selectAllCoaches,
} = coachesEntityAdapter.getSelectors(state => state.dualPanelSelector.coaches.items)

export const selectStudentsData = createSelector(
  state => state.dualPanelSelector.students,
  selectAllStudents,
  (studentsData, students) => ({
    ...studentsData,
    items: students,
  })
)

export const selectCoachesData = createSelector(
  state => state.dualPanelSelector.coaches,
  selectAllCoaches,
  (coachesData, coaches) => ({
    ...coachesData,
    items: coaches,
  })
)



const {
  selectAll: selectAllPrograms,
} = programsEntityAdapter.getSelectors(state => state.dualPanelSelector.programs.items)

const {
  selectAll: selectAllSeries,
} = seriesEntityAdapter.getSelectors(state => state.dualPanelSelector.series.items)

export const selectProgramsData = createSelector(
  state => state.dualPanelSelector.programs,
  selectAllPrograms,
  (programsData, programs) => ({
    ...programsData,
    items: programs,
  })
)

export const selectSeriesData = createSelector(
  state => state.dualPanelSelector.series,
  selectAllSeries,
  (seriesData, series) => ({
    ...seriesData,
    items: series,
  })
)

export const {
  setUnavailableStudents,
  setSearchValue,
  reset,
} = dualPanelSelectorReducer.actions

export default dualPanelSelectorReducer.reducer
